// Admin self-service TOTP 2FA (security-hardening task, item 3). Rendered
// inside pages/SettingsPage.jsx as its own section — deliberately NOT part
// of that page's settings-object patch/save flow (2FA state lives on
// `users`, not the `settings` table), so this owns its own fetch/mutation
// lifecycle via hooks/useTwoFactor.js.
//
// Three states: disabled (an "Enable" button), setup-in-progress (QR + a
// 6-digit confirm step), enabled (a "Disable" button gated by the current
// password). A successful confirm shows the ten recovery codes ONCE — the
// only moment they are ever available in plaintext (services/totpService.js)
// — with an explicit "I've saved these" acknowledgement before the panel
// returns to its normal enabled state, so the codes can't just be missed by
// scrolling past them.

import { useState } from 'react';
import { useLanguage } from '../context/LanguageContext.jsx';
import {
  useTwoFactorStatus,
  useStartTwoFactorSetup,
  useConfirmTwoFactorSetup,
  useDisableTwoFactor,
} from '../hooks/useTwoFactor.js';
import { useActionFeedback } from '../hooks/useActionFeedback.js';
import { Spinner } from 'shared/components/Feedback/index.js';
import styles from './TwoFactorSettings.module.css';

export default function TwoFactorSettings() {
  const { t } = useLanguage();
  const statusQuery = useTwoFactorStatus();
  const startSetup = useStartTwoFactorSetup();
  const confirmSetup = useConfirmTwoFactorSetup();
  const disable = useDisableTwoFactor();
  const { confirm, toastOk, toastErr } = useActionFeedback();

  const [setupData, setSetupData] = useState(null); // { secret, qrDataUrl }
  const [confirmCode, setConfirmCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [showDisableForm, setShowDisableForm] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');

  const handleStartSetup = () => {
    startSetup.mutate(undefined, {
      onSuccess: (data) => setSetupData(data),
      onError: (err) => toastErr(err),
    });
  };

  const handleConfirm = (e) => {
    e.preventDefault();
    confirmSetup.mutate(confirmCode.trim(), {
      onSuccess: (data) => {
        setRecoveryCodes(data.recoveryCodes);
        setSetupData(null);
        setConfirmCode('');
        toastOk(t('settings.twoFactor.enabledToast'));
      },
      onError: (err) => toastErr(err, ['TWO_FACTOR_INVALID', 'NO_PENDING_SETUP']),
    });
  };

  const handleDisable = async (e) => {
    e.preventDefault();
    const ok = await confirm({
      title: t('settings.twoFactor.confirmDisable.title'),
      message: t('settings.twoFactor.confirmDisable.body'),
      confirmLabel: t('settings.twoFactor.disable.button'),
      tone: 'danger',
    });
    if (!ok) return;
    disable.mutate(disablePassword, {
      onSuccess: () => {
        setShowDisableForm(false);
        setDisablePassword('');
        toastOk(t('settings.twoFactor.disabledToast'));
      },
      onError: (err) => toastErr(err, ['INVALID_CREDENTIALS']),
    });
  };

  if (statusQuery.isLoading) {
    return <p className={styles.statusDisabled}>{t('state.loading')}</p>;
  }

  // Just confirmed — show the recovery codes once, blocking the rest of the
  // panel until acknowledged.
  if (recoveryCodes) {
    return (
      <div className={styles.panel}>
        <div className={styles.recoveryCodesBox}>
          <p className={styles.warning}>{t('settings.twoFactor.recoveryCodes.warning')}</p>
          <div className={styles.recoveryCodesList}>
            {recoveryCodes.map((code) => (
              <span key={code}>{code}</span>
            ))}
          </div>
          <button type="button" className={styles.button} onClick={() => setRecoveryCodes(null)}>
            {t('settings.twoFactor.recoveryCodes.acknowledge')}
          </button>
        </div>
      </div>
    );
  }

  if (setupData) {
    return (
      <div className={styles.panel}>
        <form className={styles.setupStep} onSubmit={handleConfirm}>
          <p>{t('settings.twoFactor.setup.instructions')}</p>
          <img
            src={setupData.qrDataUrl}
            alt={t('settings.twoFactor.setup.qrAlt')}
            className={styles.qrImage}
          />
          <p className={styles.secretText}>{setupData.secret}</p>
          <label className={styles.field}>
            {t('settings.twoFactor.setup.codeLabel')}
            <input
              type="text"
              inputMode="numeric"
              className={styles.input}
              value={confirmCode}
              onChange={(e) => setConfirmCode(e.target.value)}
              autoFocus
            />
          </label>
          <div className={styles.actionsRow}>
            <button type="submit" className={styles.button} disabled={confirmSetup.isPending}>
              {confirmSetup.isPending && <Spinner />}{' '}
              {confirmSetup.isPending
                ? t('settings.twoFactor.setup.confirming')
                : t('settings.twoFactor.setup.confirm')}
            </button>
            <button
              type="button"
              className={styles.dangerButton}
              onClick={() => {
                setSetupData(null);
                setConfirmCode('');
              }}
            >
              {t('settings.twoFactor.setup.cancel')}
            </button>
          </div>
        </form>
      </div>
    );
  }

  if (statusQuery.data) {
    return (
      <div className={styles.panel}>
        <div className={styles.statusRow}>
          <span className={styles.statusEnabled}>{t('settings.twoFactor.status.enabled')}</span>
        </div>
        {!showDisableForm ? (
          <button
            type="button"
            className={styles.dangerButton}
            onClick={() => setShowDisableForm(true)}
          >
            {t('settings.twoFactor.disable.button')}
          </button>
        ) : (
          <form className={styles.setupStep} onSubmit={handleDisable}>
            <label className={styles.field}>
              {t('settings.twoFactor.disable.passwordLabel')}
              <input
                type="password"
                className={styles.input}
                value={disablePassword}
                onChange={(e) => setDisablePassword(e.target.value)}
                autoFocus
              />
            </label>
            <div className={styles.actionsRow}>
              <button type="submit" className={styles.dangerButton} disabled={disable.isPending}>
                {disable.isPending && <Spinner />}{' '}
                {disable.isPending
                  ? t('settings.twoFactor.disable.disabling')
                  : t('settings.twoFactor.disable.confirm')}
              </button>
              <button
                type="button"
                className={styles.button}
                onClick={() => {
                  setShowDisableForm(false);
                  setDisablePassword('');
                }}
              >
                {t('settings.twoFactor.disable.cancel')}
              </button>
            </div>
          </form>
        )}
      </div>
    );
  }

  return (
    <div className={styles.panel}>
      <div className={styles.statusRow}>
        <span className={styles.statusDisabled}>{t('settings.twoFactor.status.disabled')}</span>
      </div>
      <button
        type="button"
        className={styles.button}
        onClick={handleStartSetup}
        disabled={startSetup.isPending}
      >
        {startSetup.isPending && <Spinner />}{' '}
        {startSetup.isPending
          ? t('settings.twoFactor.enable.starting')
          : t('settings.twoFactor.enable.button')}
      </button>
    </div>
  );
}
